import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import TenantDetailPage from './pages/TenantDetailPage';
import SettingsPage from './pages/SettingsPage';
import ReportsPage from './pages/ReportsPage';
import DecisionEnginePage from './pages/DecisionEnginePage';
import RevenueEnginePage from './pages/RevenueEnginePage';
import PaymentPortalPage from './pages/PaymentPortalPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-tenafi-green border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading Tenafi...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
}

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="text-4xl font-bold text-tenafi-green mb-4">404</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Page not found</h1>
        <p className="text-gray-500 mb-6">The page you are looking for does not exist.</p>
        <Link
          to="/"
          className="px-6 py-3 bg-tenafi-green text-white rounded-xl font-semibold hover:bg-tenafi-green-dark transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const showNavbar = user && !location.pathname.startsWith('/pay/');

  return (
    <>
      {showNavbar && <Navbar />}
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
        <Route path="/tenant/:propertyId/:tenantId" element={<ProtectedRoute><TenantDetailPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/engine" element={<ProtectedRoute><DecisionEnginePage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/revenue" element={<ProtectedRoute><RevenueEnginePage /></ProtectedRoute>} />
        <Route path="/pay/:propertyId/:tenantId" element={<PaymentPortalPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

function AppContent() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
